// 🔧 store_orders.js
// ✅ Purpose: Manager-facing Store order fulfillment queue
// ✅ Features:
// - Loads Store orders from /events/{eventId}/orders
// - Combines Store, ticket, sponsor, vendor, and auction orders in one page
// - Shows customer, items, fulfillment method, status, totals, and shipping address
// - Allows managers/admins to update fulfillment status through Cloud Function
// - Supports tracking number, shipping carrier, and fulfillment notes
// - Shows success and error notifications for 20 seconds
// - Fully block-commented for clarity and future devs

import React, { useEffect, useState } from "react";
import {
  collection,
  getDocs,
  orderBy,
  query,
} from "firebase/firestore";
import { getFunctions, httpsCallable } from "firebase/functions";
import { Link } from "react-router-dom";
import { db } from "../../firebase";
import { useEventContext } from "../../hooks/use_event_context";
import FullPageSpinner from "../../components/FullPageSpinner";
import { logger } from "../../utils/logger";
import { hasPurchasedAddOn } from "../../utils/event_addons";
import AddonRequired from "../../components/AddonRequired";

const StoreOrders = () => {

  const {
    eventslug,
    eventId,
    eventDoc,
    eventLoading,
    ready,
  } = useEventContext({ redirectIfNotFound: true });

  const storeEnabled = hasPurchasedAddOn(eventDoc, "store");
  const ticketsEnabled = hasPurchasedAddOn(eventDoc, "tickets");
  const auctionEnabled = hasPurchasedAddOn(eventDoc, "auction");
  const commerceEnabled = storeEnabled || ticketsEnabled || auctionEnabled;

  const [orders, setOrders] = useState([]);
  const [ticketsById, setTicketsById] = useState({});
  const [statusFilter, setStatusFilter] = useState("all");
  const [orderTypeFilter, setOrderTypeFilter] = useState("all");
  const [updatingOrderId, setUpdatingOrderId] = useState("");
  const [fulfillmentEdits, setFulfillmentEdits] = useState({});

  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState(null);
  const [error, setError] = useState(null);

  const centsToMoney = (cents) =>
    `$${(Number(cents || 0) / 100).toFixed(2)}`;

  const formatDate = (value) => {
    const date = value?.toDate ? value.toDate() : value ? new Date(value) : null;
    return date && !Number.isNaN(date.getTime())
      ? date.toLocaleString()
      : "Not available";
  };

  const getOrderTitle = (order) => {
    if (order.orderType === "ticket") {
      return ticketsById[order.ticketId]?.title || order.ticketTitle || "Ticket Order";
    }
    if (order.orderType === "sponsor") return order.sponsorName || "Sponsor Payment";
    if (order.orderType === "vendor") return order.vendorName || "Vendor Payment";
    if (order.orderType === "auction") return order.auctionItemTitle || "Auction Payment";
    return order.title || "Event Order";
  };

  useEffect(() => {
    if (!status && !error) return;

    const timer = setTimeout(() => {
      setStatus(null);
      setError(null);
    }, 20000);

    return () => clearTimeout(timer);
  }, [status, error]);

  const loadOrders = async () => {
    if (!eventId) return;

    setLoading(true);

    try {
      const [ordersSnap, ticketsSnap] = await Promise.all([
        getDocs(
          query(
            collection(db, "events", eventId, "orders"),
            orderBy("createdAt", "desc")
          )
        ),
        getDocs(collection(db, "events", eventId, "tickets")),
      ]);

      const list = ordersSnap.docs.map((docSnap) => ({
        id: docSnap.id,
        ...docSnap.data(),
      }));

      setOrders(list);
      setTicketsById(
        Object.fromEntries(
          ticketsSnap.docs.map((ticketDoc) => [
            ticketDoc.id,
            { id: ticketDoc.id, ...ticketDoc.data() },
          ])
        )
      );

      const initialEdits = {};

      list.filter((order) => order.orderType === "store").forEach((order) => {
        initialEdits[order.id] = {
          fulfillmentStatus:
            order.fulfillmentStatus ||
            (order.fulfillmentMethod === "shipping"
              ? "awaiting_shipment"
              : "awaiting_pickup"),
          trackingNumber: order.trackingNumber || "",
          shippingCarrier: order.shippingCarrier || "",
          fulfillmentNote: order.fulfillmentNote || "",
        };
      });

      setFulfillmentEdits(initialEdits);

      logger.log("✅ Store orders loaded", {
        eventId,
        count: list.length,
      });
    } catch (err) {
      logger.error("❌ Failed loading Store orders:", {
        value: err,
      });

      setError("Unable to load Store orders. A Firestore index may be needed for orderType + createdAt.");
      setOrders([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!ready || !eventId) return;
    loadOrders();
  }, [ready, eventId]);

  const updateEdit = (orderId, field, value) => {
    setFulfillmentEdits((prev) => ({
      ...prev,
      [orderId]: {
        ...(prev[orderId] || {}),
        [field]: value,
      },
    }));
  };

  const updateFulfillment = async (orderId) => {
    setStatus(null);
    setError(null);
    setUpdatingOrderId(orderId);

    try {
      const functions = getFunctions();
      const updateStoreOrderFulfillment = httpsCallable(
        functions,
        "updateStoreOrderFulfillment"
      );

      await updateStoreOrderFulfillment({
        eventId,
        orderId,
        ...(fulfillmentEdits[orderId] || {}),
      });

      setStatus("✅ Store order updated.");
      await loadOrders();
    } catch (err) {
      logger.error("❌ Failed updating Store order:", {
        value: err,
      });

      setError(err?.message || "Unable to update Store order.");
    } finally {
      setUpdatingOrderId("");
    }
  };

    const getFulfillmentBadgeClass = (status) => {
    switch (status) {
        case "awaiting_pickup":
        return "bg-warning text-dark";

        case "awaiting_shipment":
        return "bg-info text-dark";

        case "picked_up":
        return "bg-success";

        case "shipped":
        return "bg-primary";

        case "completed":
        return "bg-success";

        case "cancelled":
        return "bg-danger";

        default:
        return "bg-secondary";
    }
    };

  const storeOrders = orders.filter((order) => order.orderType === "store");
  const otherOrders = orders.filter((order) => order.orderType !== "store");
  const filteredOtherOrders = otherOrders.filter(
    (order) => orderTypeFilter === "all" || order.orderType === orderTypeFilter
  );
  const paidOrders = orders.filter(
    (order) => order.status === "paid" || order.paymentStatus === "paid"
  );
  const revenueFor = (type) => paidOrders
    .filter((order) => !type || order.orderType === type)
    .reduce(
      (sum, order) => sum + Number(order.totalAmount || order.amountCents || 0),
      0
    );
  const pendingOrderCount = orders.filter(
    (order) => ["pending", "checkout_started"].includes(
      order.status || order.paymentStatus
    )
  ).length;
  const filteredOrders = storeOrders.filter((order) => {
    if (statusFilter === "all") return true;

    return (
      (order.fulfillmentStatus || "awaiting_pickup") === statusFilter
    );
  });

  if (
    ready &&
    eventDoc &&
    !commerceEnabled
  ) {
    return (
      <AddonRequired
        eventslug={eventslug}
        title="Commerce Power Up Required"
        description="Enable the Event Merch Store or Tickets Power Up before managing orders."
      />
    );
  }

  if (!ready || !eventId || eventLoading || loading) {
    return <FullPageSpinner message="Loading Store Orders..." />;
  }
  
  return (
    <section id="top-content" className="container-fluid py-5">
      <div className="container" style={{ maxWidth: "1300px" }}>
        {status && <div className="alert alert-success">{status}</div>}
        {error && <div className="alert alert-danger">{error}</div>}

        <div className="row mb-4">
          <div className="col text-start">
            <h1 className="display-5">📦 Store Orders</h1>
            <p className="lead text-muted mb-0">
              Payment and fulfillment activity for {eventDoc?.name || "this event"}.
            </p>
          </div>
        </div>

        <div className="card shadow-sm border-0 mb-4">
          <div className="card-body d-flex flex-column flex-md-row gap-2 justify-content-between">
            <div>
              <strong>{orders.length}</strong> total order
              {orders.length === 1 ? "" : "s"} ({storeOrders.length} Store)
            </div>

            <div className="d-flex flex-wrap gap-2">
              {storeEnabled && <select
                className="form-select form-select-sm"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <option value="all">All Orders</option>
                <option value="awaiting_pickup">Awaiting Pickup</option>
                <option value="awaiting_shipment">Awaiting Shipment</option>
                <option value="picked_up">Picked Up</option>
                <option value="shipped">Shipped</option>
                <option value="completed">Completed</option>
                <option value="cancelled">Cancelled</option>
              </select>}

              {storeEnabled && <Link
                to={`/${eventslug}/store_products`}
                className="btn btn-outline-primary btn-sm"
              >
                Products
              </Link>}

              {storeEnabled && <Link
                to={`/${eventslug}/store`}
                className="btn btn-outline-dark btn-sm"
              >
                Public Store
              </Link>}
            </div>
          </div>
        </div>

        <div className="row g-3 mb-4">
          {[
            ["Total Paid Revenue", centsToMoney(revenueFor())],
            ["Ticket Revenue", centsToMoney(revenueFor("ticket"))],
            ["Store Revenue", centsToMoney(revenueFor("store"))],
            ["Pending Orders", pendingOrderCount],
          ].map(([label, value]) => (
            <div className="col-12 col-md-6 col-xl-3" key={label}>
              <div className="card h-100 shadow-sm border-0">
                <div className="card-body">
                  <div className="text-muted small">{label}</div>
                  <div className="fs-3 fw-bold">{value}</div>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="card shadow-sm border-0 mb-4">
          <div className="card-header bg-white d-flex flex-column flex-md-row gap-2 justify-content-between align-items-md-center">
            <strong>Ticket and Event Orders</strong>
            <select
              className="form-select form-select-sm"
              style={{ maxWidth: "220px" }}
              value={orderTypeFilter}
              onChange={(event) => setOrderTypeFilter(event.target.value)}
            >
              <option value="all">All Types</option>
              <option value="ticket">Tickets</option>
              <option value="sponsor">Sponsors</option>
              <option value="vendor">Vendors</option>
              <option value="auction">Auctions</option>
            </select>
          </div>
          <div className="card-body">
            {filteredOtherOrders.length === 0 ? (
              <div className="alert alert-info mb-0">
                No ticket or other event orders have been created.
              </div>
            ) : (
              <div className="table-responsive">
                <table className="table align-middle mb-0">
                  <thead>
                    <tr>
                      <th>Created</th>
                      <th>Type</th>
                      <th>Item</th>
                      <th>Buyer</th>
                      <th>Quantity</th>
                      <th>Amount</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredOtherOrders.map((order) => (
                      <tr key={order.id}>
                        <td>{formatDate(order.createdAt)}</td>
                        <td className="text-capitalize">{order.orderType || "order"}</td>
                        <td>{getOrderTitle(order)}</td>
                        <td>{order.buyerEmail || order.checkoutInfo?.email || "Not available"}</td>
                        <td>{Number(order.quantity || 1)}</td>
                        <td>{centsToMoney(order.totalAmount || order.amountCents || 0)}</td>
                        <td>
                          <span className={`badge ${
                            order.status === "paid" || order.paymentStatus === "paid"
                              ? "bg-success"
                              : "bg-warning text-dark"
                          }`}>
                            {order.status || order.paymentStatus || "pending"}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>

        <h2 className="h4 mb-3">Store Fulfillment</h2>

        {filteredOrders.length === 0 ? (
          <div className="card shadow-sm border-0">
            <div className="card-body">
              <div className="alert alert-info mb-0">
                No Store orders match this filter.
              </div>
            </div>
          </div>
        ) : (
          <div className="row g-4">
            {filteredOrders.map((order) => {
              const cartItems = Array.isArray(order.cartItems)
                ? order.cartItems
                : [];

              const checkoutInfo = order.checkoutInfo || {};
              const edit = fulfillmentEdits[order.id] || {};

              return (
                <div key={order.id} className="col-12">
                  <div className="card shadow-sm border-0">
                    <div className="card-header bg-white d-flex flex-column flex-lg-row justify-content-between gap-2">
                      <div>
                        <strong>
                          Order {order.id.slice(0, 8)}
                        </strong>

                        <div className="small text-muted">
                          {order.createdAt?.toDate
                            ? order.createdAt.toDate().toLocaleString()
                            : "Recently"}
                        </div>
                      </div>

                      <div className="d-flex flex-wrap gap-2">
                        <span className="badge bg-dark">
                          {order.status || "pending"}
                        </span>

                        <span className="badge bg-primary">
                          {order.fulfillmentMethod || "pickup"}
                        </span>

                        <span
                        className={`badge ${getFulfillmentBadgeClass(
                            order.fulfillmentStatus
                        )}`}
                        >
                        {order.fulfillmentStatus || "awaiting_pickup"}
                        </span>

                        <span className="badge bg-success">
                          {centsToMoney(order.totalAmount || order.amountCents || 0)}
                        </span>
                      </div>
                    </div>

                    <div className="card-body">
                      <div className="row g-4">
                        <div className="col-12 col-lg-4">
                          <h6 className="fw-bold">Customer</h6>

                          <div className="border rounded bg-light p-3 small">
                            <div>
                              <strong>Name:</strong>{" "}
                              {order.customerName || checkoutInfo.customerName || "N/A"}
                            </div>

                            <div>
                              <strong>Email:</strong>{" "}
                              {order.buyerEmail || checkoutInfo.email || "N/A"}
                            </div>

                            <div>
                              <strong>Phone:</strong>{" "}
                              {order.buyerPhone || checkoutInfo.phone || "N/A"}
                            </div>

                            {checkoutInfo.orderNotes && (
                              <>
                                <hr />
                                <div>
                                  <strong>Notes:</strong>
                                  <br />
                                  {checkoutInfo.orderNotes}
                                </div>
                              </>
                            )}
                          </div>

                          {order.fulfillmentMethod === "shipping" && (
                            <div
                            className="border rounded bg-light p-3 small mt-3"
                            style={{
                                fontFamily: "monospace",
                                whiteSpace: "pre-line",
                            }}
                            >
                              <h6 className="fw-bold">Shipping Address</h6>

                              <div>
                                {checkoutInfo.shippingName || order.customerName}
                              </div>
                              <div>{checkoutInfo.shippingAddress1}</div>
                              <div>{checkoutInfo.shippingAddress2}</div>
                              <div>
                                {checkoutInfo.shippingCity},{" "}
                                {checkoutInfo.shippingState}{" "}
                                {checkoutInfo.shippingPostalCode}
                              </div>
                            </div>
                          )}
                        </div>

                        <div className="col-12 col-lg-4">
                          <h6 className="fw-bold">Items</h6>

                          <div className="list-group">
                            {cartItems.map((item, index) => (
                              <div
                                key={`${item.productId}-${item.variantId}-${index}`}
                                className="list-group-item"
                              >
                                <div className="fw-bold">
                                  {item.productName}
                                </div>

                                <div className="small text-muted">
                                  {item.variantLabel}
                                </div>

                                <div className="small">
                                  Qty {item.quantity} × {centsToMoney(item.priceCents)}
                                </div>

                                {item.sku && (
                                  <div className="small text-muted">
                                    SKU: {item.sku}
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>

                          <div className="border rounded bg-light p-3 small mt-3">
                            <div className="d-flex justify-content-between">
                              <span>Subtotal</span>
                              <strong>{centsToMoney(order.subtotalCents || 0)}</strong>
                            </div>

                            <div className="d-flex justify-content-between">
                              <span>Shipping</span>
                              <strong>{centsToMoney(order.shippingCents || 0)}</strong>
                            </div>

                            <hr />

                            <div className="d-flex justify-content-between">
                              <span>Total</span>
                              <strong>{centsToMoney(order.totalAmount || order.amountCents || 0)}</strong>
                            </div>
                          </div>
                        </div>

                        <div className="col-12 col-lg-4">
                          <h6 className="fw-bold">Fulfillment</h6>

                          <div className="mb-3">
                            <label className="form-label">Status</label>

                            <select
                              className="form-select"
                              value={edit.fulfillmentStatus || ""}
                              onChange={(e) =>
                                updateEdit(
                                  order.id,
                                  "fulfillmentStatus",
                                  e.target.value
                                )
                              }
                            >
                              <option value="awaiting_pickup">
                                Awaiting Pickup
                              </option>
                              <option value="awaiting_shipment">
                                Awaiting Shipment
                              </option>
                              <option value="picked_up">
                                Picked Up
                              </option>
                              <option value="shipped">
                                Shipped
                              </option>
                              <option value="completed">
                                Completed
                              </option>
                              <option value="cancelled">
                                Cancelled
                              </option>
                            </select>
                          </div>

                          <div className="mb-3">
                            <label className="form-label">Carrier</label>

                            <input
                              className="form-control"
                              value={edit.shippingCarrier || ""}
                              onChange={(e) =>
                                updateEdit(
                                  order.id,
                                  "shippingCarrier",
                                  e.target.value
                                )
                              }
                              placeholder="USPS, UPS, FedEx"
                            />
                          </div>

                          <div className="mb-3">
                            <label className="form-label">Tracking Number</label>

                            <input
                              className="form-control"
                              value={edit.trackingNumber || ""}
                              onChange={(e) =>
                                updateEdit(
                                  order.id,
                                  "trackingNumber",
                                  e.target.value
                                )
                              }
                            />
                          </div>

                            {edit.trackingNumber && (
                            <div className="small mt-2">
                                <a
                                href={`https://www.google.com/search?q=${encodeURIComponent(
                                    `${edit.shippingCarrier || ""} ${edit.trackingNumber}`
                                )}`}
                                target="_blank"
                                rel="noreferrer"
                                >
                                🔍 Track Package
                                </a>
                            </div>
                            )}

                          <div className="mb-3">
                            <label className="form-label">Fulfillment Note</label>

                            <textarea
                              className="form-control"
                              rows={3}
                              value={edit.fulfillmentNote || ""}
                              onChange={(e) =>
                                updateEdit(
                                  order.id,
                                  "fulfillmentNote",
                                  e.target.value
                                )
                              }
                            />
                          </div>

                          <button
                            type="button"
                            className="btn btn-primary w-100"
                            disabled={updatingOrderId === order.id}
                            onClick={() => updateFulfillment(order.id)}
                          >
                            {updatingOrderId === order.id
                              ? "Saving..."
                              : "Save Fulfillment"}
                          </button>

                            {edit.fulfillmentStatus !== "completed" && (
                            <button
                                type="button"
                                className="btn btn-outline-success w-100 mt-2"
                                onClick={() => {
                                updateEdit(
                                    order.id,
                                    "fulfillmentStatus",
                                    "completed"
                                );
                                }}
                            >
                                Mark Completed
                            </button>
                            )}

                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </section>
  );
};

export default StoreOrders;
